import { useCallback, useMemo } from 'react';

import type { CatalogItem } from '@/entities/catalog-item';
import type { CountedLabelFunction } from '../../../constants';
import { alphabeticallySortedFilters, labeledFilterColumns } from '../../../constants';
import { countOccurrence } from './lib/countOccurrence';

import type { Row, Table } from '@tanstack/react-table';

type FilterOption = { label: string; count: number };

const useTableFilters = (table: Table<CatalogItem>) => {
  const filteredRows: Array<Row<CatalogItem>> = table.getFilteredRowModel().rows;

  const getCountedLabels: CountedLabelFunction = useCallback(
    (key: string, joined?: boolean): FilterOption[] => {
      const counts: { [label: string]: number } = {};
      for (const row of filteredRows) {
        if (joined) {
          const label: string | undefined = row.getValue(key);
          const labels: string[] | undefined = label?.split(',');
          for (const l of labels ?? []) {
            countOccurrence(counts, l);
          }
        } else {
          const label: string | undefined = row.getValue(key);
          if (label) countOccurrence(counts, label);
        }
      }

      return Object.entries(counts)
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => {
          if (alphabeticallySortedFilters.includes(key)) {
            return a.label.localeCompare(b.label);
          }
          return b.count - a.count;
        });
    },
    [filteredRows]
  );

  const columnFiltersWithCounts = useMemo(
    () =>
      Object.entries(labeledFilterColumns).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value(getCountedLabels)
        }),
        {} as Record<string, FilterOption[]>
      ),
    [getCountedLabels]
  );

  return {
    columnFiltersWithCounts
  };
};

export default useTableFilters;
